import { clickToWgs84, wgs84CoordsToDisplay } from './coords';

export function haversineMeters(a, b) {
  const R = 6371000;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

export function pathLengthMeters(coords) {
  let sum = 0;
  for (let i = 1; i < coords.length; i += 1) {
    sum += haversineMeters(
      { lng: coords[i - 1][0], lat: coords[i - 1][1] },
      { lng: coords[i][0], lat: coords[i][1] }
    );
  }
  return sum;
}

/** 球面多边形近似面积（平方米） */
export function polygonAreaMeters(coords) {
  if (!coords || coords.length < 3) return 0;
  const R = 6371000;
  let total = 0;
  const ring = [...coords];
  if (ring[0][0] !== ring[ring.length - 1][0] || ring[0][1] !== ring[ring.length - 1][1]) {
    ring.push(ring[0]);
  }
  for (let i = 0; i < ring.length - 1; i += 1) {
    const [lng1, lat1] = ring[i];
    const [lng2, lat2] = ring[i + 1];
    total +=
      ((lng2 * Math.PI) / 180 - (lng1 * Math.PI) / 180) *
      (2 + Math.sin((lat1 * Math.PI) / 180) + Math.sin((lat2 * Math.PI) / 180));
  }
  return Math.abs((total * R * R) / 2);
}

export function formatDistance(m) {
  if (m < 1000) return `${m.toFixed(1)} 米`;
  return `${(m / 1000).toFixed(2)} 公里`;
}

/** 合并连续重复点（双击结束时常见），避免 0 米分段 */
export function dedupeConsecutiveCoords(coords, minMeters = 0.5) {
  if (!coords || coords.length === 0) return [];
  const out = [[coords[0][0], coords[0][1]]];
  for (let i = 1; i < coords.length; i += 1) {
    const prev = out[out.length - 1];
    const cur = coords[i];
    const d = haversineMeters(
      { lng: prev[0], lat: prev[1] },
      { lng: cur[0], lat: cur[1] }
    );
    if (d >= minMeters) out.push([cur[0], cur[1]]);
  }
  return out;
}

export function formatArea(sq) {
  if (sq < 10000) return `${sq.toFixed(1)} 平方米`;
  return `${(sq / 10000).toFixed(2)} 公顷`;
}

/** 区域框顶点（去重且不强制闭合） */
export function openRingCoords(coords) {
  const clean = dedupeConsecutiveCoords(coords, 0.5);
  if (clean.length < 1) return [];
  const first = clean[0];
  const last = clean[clean.length - 1];
  if (
    clean.length > 1 &&
    Math.abs(first[0] - last[0]) < 1e-9 &&
    Math.abs(first[1] - last[1]) < 1e-9
  ) {
    return clean.slice(0, -1);
  }
  return clean;
}

/** 简单顶点平均，用于面积标注落点 */
export function ringCentroid(coords) {
  const ring = openRingCoords(coords);
  if (ring.length === 0) return null;
  let sumLng = 0;
  let sumLat = 0;
  ring.forEach((c) => {
    sumLng += c[0];
    sumLat += c[1];
  });
  return [sumLng / ring.length, sumLat / ring.length];
}

/** 矩形两点 → 四角环（不闭合） */
export function rectangleRingFromCorners(a, b) {
  return [
    [a[0], a[1]],
    [b[0], a[1]],
    [b[0], b[1]],
    [a[0], b[1]]
  ];
}

export function uid(prefix = 'f') {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

/** 像素空间：点到线段最近点 */
export function screenClosestOnSegment(px, a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  if (len2 < 1e-6) {
    return {
      t: 0,
      point: { x: a.x, y: a.y },
      dist: Math.hypot(px.x - a.x, px.y - a.y)
    };
  }
  let t = ((px.x - a.x) * dx + (px.y - a.y) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  const point = { x: a.x + t * dx, y: a.y + t * dy };
  return {
    t,
    point,
    dist: Math.hypot(px.x - point.x, px.y - point.y)
  };
}

/**
 * 在草稿/已定折线上做命中：优先顶点，其次线段（用于插入点，避免折返）
 * @param {{ vertexPx?: number, linePx?: number, closed?: boolean }} options
 * @returns {{ type: 'vertex', index: number } | { type: 'edge', index: number, wgs: number[] } | null}
 */
export function findPolylineHit(map, coordsWgs, screenPoint, options = {}) {
  if (!map || !coordsWgs || coordsWgs.length === 0 || !screenPoint) return null;
  const vertexPx = options.vertexPx ?? 14;
  const linePx = options.linePx ?? 10;
  const closed = !!options.closed && coordsWgs.length >= 3;
  const displayCoords = wgs84CoordsToDisplay(coordsWgs, map);

  let bestVertex = null;
  for (let i = 0; i < displayCoords.length; i += 1) {
    const p = map.project(displayCoords[i]);
    const dist = Math.hypot(p.x - screenPoint.x, p.y - screenPoint.y);
    if (dist <= vertexPx && (!bestVertex || dist < bestVertex.dist)) {
      bestVertex = { type: 'vertex', index: i, dist };
    }
  }
  if (bestVertex) return { type: 'vertex', index: bestVertex.index };

  let bestEdge = null;
  const edgeCount = closed ? displayCoords.length : Math.max(0, displayCoords.length - 1);
  for (let i = 0; i < edgeCount; i += 1) {
    const a = map.project(displayCoords[i]);
    const b = map.project(displayCoords[(i + 1) % displayCoords.length]);
    const closest = screenClosestOnSegment(screenPoint, a, b);
    // 靠近端点时交给顶点逻辑，避免重复点
    if (closest.t < 0.04 || closest.t > 0.96) continue;
    if (closest.dist <= linePx && (!bestEdge || closest.dist < bestEdge.dist)) {
      const lngLat = map.unproject(closest.point);
      const wgs = clickToWgs84(lngLat);
      bestEdge = { type: 'edge', index: i, dist: closest.dist, wgs };
    }
  }
  return bestEdge;
}
